/* eslint-disable no-unused-vars */
import React from 'react';
import { useNavigate } from 'react-router-dom';
import { Box, Flex, Text, Badge, Icon, IconButton, Tooltip, useDisclosure } from '@chakra-ui/react';
import {
  Buildings,
  MapPin,
  Tag,
  Calculator,
  IdentificationCard,
  PencilSimple,
  Star,
  WarningCircle,
  NotePencil,
  ChartBar,
  FolderOpen,
} from 'phosphor-react';
import { ICompany } from '../../hooks/useCompanies';
import { ICompanyFieldValueSimple } from '../../hooks/useCompanyFields';
import ViewNotaModal from '../viewNota';
import { formatCPFOrCNPJ, getDocumentType } from '../../utils/FormatFunctions';
import { EmpresaDetailModal } from '../../pages/empresaDashboard/components';

interface CompanyCardProps {
  company: ICompany;
  onClick: (company: ICompany) => void;
  onEditCharacteristics?: (company: ICompany) => void;
  showNotesButton?: boolean;
  showDashboardButton?: boolean;
  showDossieButton?: boolean;
}

export function CompanyCard({
  company,
  onClick,
  onEditCharacteristics,
  showNotesButton = false,
  showDashboardButton = false,
  showDossieButton = false,
}: CompanyCardProps) {
  const navigate = useNavigate();
  const {
    isOpen: isDashboardOpen,
    onOpen: onDashboardOpen,
    onClose: onDashboardClose,
  } = useDisclosure();

  // Usar campos_valores que já vem na listagem de empresas (evita N+1 queries)
  const fieldValues = Array.isArray(company.campos_valores) ? company.campos_valores : [];

  // Retorna o status real da empresa baseado no campo 'ativa' e dt_cli_ate
  const getStatus = ():
    | 'ativa'
    | 'inativa'
    | 'processo_off'
    | 'fora_acessorias'
    | 'fora_dominio' => {
    const { ativa, dt_cli_ate: dtCliAte } = company;

    // Verifica status especiais primeiro
    if (ativa === 'ForaDoAcessorias') return 'fora_acessorias';
    if (ativa === 'ForaDaDominio') return 'fora_dominio';
    if (ativa === 'Inativa') return 'inativa';

    // Verifica se está ativa
    const isAtivaFlag =
      ativa === true ||
      ativa === 1 ||
      ativa === 'Ativa' ||
      ativa === 'Sim' ||
      (typeof ativa === 'string' && ativa.toLowerCase().trim() === 'ativa');

    // Se ativa é vazio, null ou undefined, considera inativa
    if (!isAtivaFlag || !ativa || ativa === '') return 'inativa';

    // Se está marcada como ativa, verifica dt_cli_ate
    if (!dtCliAte) return 'ativa';

    // Calcula o último dia do mês seguinte a dt_cli_ate
    const dtCliAteDate = new Date(dtCliAte);
    const ultimoDiaMesSeguinte = new Date(
      dtCliAteDate.getFullYear(),
      dtCliAteDate.getMonth() + 2,
      0,
    );
    const hoje = new Date();
    hoje.setHours(0, 0, 0, 0);

    // Se data atual > último dia do mês seguinte, está em processo off
    return hoje <= ultimoDiaMesSeguinte ? 'ativa' : 'processo_off';
  };

  const getStatusLabel = () => {
    const status = getStatus();
    switch (status) {
      case 'ativa':
        return 'Ativa';
      case 'inativa':
        return 'Inativa';
      case 'processo_off':
        return 'Processo off';
      case 'fora_acessorias':
        return 'Fora do Acessórias';
      case 'fora_dominio':
        return 'Fora da Domínio';
      default:
        return 'Inativa';
    }
  };

  const getStatusColor = () => {
    const status = getStatus();
    switch (status) {
      case 'ativa':
        return 'green';
      case 'inativa':
        return 'red';
      case 'processo_off':
        return 'yellow';
      case 'fora_acessorias':
        return 'orange';
      case 'fora_dominio':
        return 'purple';
      default:
        return 'red';
    }
  };

  // Check for data mismatches - agora usa os indicadores calculados pelo backend
  const hasDivergencia = () => {
    return (
      company.div_cnpj_invalido ||
      company.div_razao_social ||
      company.div_cnpj ||
      company.div_cidade ||
      company.div_uf
    );
  };

  // Conta quantas divergências existem
  const countDivergencias = () => {
    return [
      company.div_cnpj_invalido,
      company.div_razao_social,
      company.div_cnpj,
      company.div_cidade,
      company.div_uf,
    ].filter(Boolean).length;
  };

  // Gera o texto do tooltip com as divergências específicas
  const getDivergenciaTooltip = () => {
    const divergencias: string[] = [];
    if (company.div_cnpj_invalido) divergencias.push('CNPJ inválido ou ausente');
    if (company.div_razao_social)
      divergencias.push(
        `Razão Social: "${company.razao_social || 'N/A'}" ≠ GOV: "${company.gov_razao_social || 'N/A'}"`,
      );
    if (company.div_cnpj)
      divergencias.push(`CNPJ: "${company.cnpj || 'N/A'}" ≠ GOV: "${company.gov_cnpj || 'N/A'}"`);
    if (company.div_cidade)
      divergencias.push(
        `Cidade: "${company.cidade || 'N/A'}" ≠ GOV: "${company.gov_cidade || 'N/A'}"`,
      );
    if (company.div_uf)
      divergencias.push(`UF: "${company.uf || 'N/A'}" ≠ GOV: "${company.gov_uf || 'N/A'}"`);
    return divergencias.join('\n');
  };

  const getCnpjTooltipLabel = () => {
    if (company.div_cnpj) {
      return `GOV: ${company.gov_cnpj ? formatCPFOrCNPJ(company.gov_cnpj) : 'N/A'}`;
    }
    if (company.div_cnpj_invalido) {
      return 'CNPJ inválido ou ausente';
    }
    return '';
  };

  const handleEditClick = (e: React.MouseEvent) => {
    e.stopPropagation();
    if (onEditCharacteristics) {
      onEditCharacteristics(company);
    }
  };

  const handleDashboardClick = (e: React.MouseEvent) => {
    e.stopPropagation();
    onDashboardOpen();
  };

  const handleDossieClick = (e: React.MouseEvent) => {
    e.stopPropagation();
    if (company?.external_id) {
      navigate(`/dossie-empresa/${company.external_id}`, {
        state: { company },
      });
    }
  };

  // Datas para o modal de anotações (usando datas padrão)
  const today = new Date();
  const dateEnd = today.toISOString().split('T')[0];
  const dateStart = new Date(today.getFullYear(), today.getMonth(), 1).toISOString().split('T')[0];

  const hasActionButtons =
    showNotesButton || onEditCharacteristics || showDashboardButton || showDossieButton;

  return (
    <Box
      bg="white"
      borderRadius="xl"
      border="1px"
      borderColor="gray.200"
      cursor="pointer"
      transition="all 0.2s"
      _hover={{
        borderColor: 'blue.300',
        boxShadow: 'md',
        transform: 'translateY(-2px)',
      }}
      onClick={() => onClick(company)}
      overflow="hidden"
    >
      <Flex align="stretch" h="100%">
        {/* Conteúdo principal */}
        <Box flex="1" p={4} minW={0}>
          {/* Header com ID, Divergências e Status */}
          <Flex justify="space-between" align="center" mb={3}>
            <Flex align="center" gap={2}>
              <Icon as={IdentificationCard} color="gray.500" boxSize={4} />
              <Text fontSize="sm" fontWeight="bold" color="gray.600">
                ID: {company.external_id}
              </Text>
              {/* Divergências - ao lado do ID */}
              {hasDivergencia() && (
                <Tooltip
                  label={getDivergenciaTooltip()}
                  placement="top"
                  hasArrow
                  whiteSpace="pre-line"
                >
                  <Badge colorScheme="red" fontSize="xs" display="flex" alignItems="center" gap={1}>
                    <Icon as={WarningCircle} weight="fill" boxSize={3} />
                    {countDivergencias()} Divergência{countDivergencias() > 1 ? 's' : ''}
                  </Badge>
                </Tooltip>
              )}
            </Flex>
            <Badge colorScheme={getStatusColor()} fontSize="xs" px={2} borderRadius="full">
              {getStatusLabel()}
            </Badge>
          </Flex>

          {/* Razão Social */}
          <Tooltip
            label={company.div_razao_social ? `GOV: ${company.gov_razao_social || 'N/A'}` : ''}
            placement="top"
            hasArrow
            isDisabled={!company.div_razao_social}
          >
            <Flex align="center" gap={2} mb={2}>
              <Icon
                as={company.div_razao_social ? WarningCircle : Buildings}
                color={company.div_razao_social ? 'red.500' : 'blue.500'}
                boxSize={5}
                weight={company.div_razao_social ? 'fill' : 'regular'}
              />
              <Text
                fontSize="md"
                fontWeight="semibold"
                color={company.div_razao_social ? 'red.600' : 'gray.800'}
                noOfLines={2}
              >
                {company.razao_social || company.nome}
              </Text>
            </Flex>
          </Tooltip>

          {/* CPF/CNPJ */}
          <Tooltip
            label={getCnpjTooltipLabel()}
            placement="top"
            hasArrow
            isDisabled={!company.div_cnpj && !company.div_cnpj_invalido}
          >
            <Text
              fontSize="sm"
              color={company.div_cnpj || company.div_cnpj_invalido ? 'red.600' : 'gray.600'}
              mb={3}
              fontFamily="mono"
              borderBottom={company.div_cnpj || company.div_cnpj_invalido ? '2px dashed' : 'none'}
              borderColor="red.300"
              display="inline-block"
            >
              {company.cnpj
                ? `${getDocumentType(company.cnpj)}: ${formatCPFOrCNPJ(company.cnpj)}`
                : 'CPF/CNPJ não informado'}
              {(company.div_cnpj || company.div_cnpj_invalido) && (
                <Icon as={WarningCircle} color="red.500" boxSize={3} ml={1} weight="fill" />
              )}
            </Text>
          </Tooltip>

          {/* Grid de informações */}
          <Box borderTop="1px" borderColor="gray.100" pt={3}>
            <Flex wrap="wrap" gap={2}>
              {/* Grupo */}
              {company.grupo_de_empresas && (
                <Flex align="center" gap={1} bg="purple.50" px={2} py={1} borderRadius="md">
                  <Icon as={Tag} color="purple.500" boxSize={3} />
                  <Text fontSize="xs" color="purple.700" fontWeight="medium" noOfLines={1}>
                    {company.grupo_de_empresas}
                  </Text>
                </Flex>
              )}

              {/* Regime Tributário */}
              {company.regime_tributario_atualizado && (
                <Flex align="center" gap={1} bg="blue.50" px={2} py={1} borderRadius="md">
                  <Icon as={Calculator} color="blue.500" boxSize={3} />
                  <Text fontSize="xs" color="blue.700" fontWeight="medium" noOfLines={1}>
                    {company.regime_tributario_atualizado}
                  </Text>
                </Flex>
              )}

              {/* Cidade/Estado */}
              {(company.cidade || company.uf) && (
                <Tooltip
                  label={
                    company.div_cidade || company.div_uf
                      ? `GOV: ${[company.gov_cidade, company.gov_uf].filter(Boolean).join(' - ') || 'N/A'}`
                      : ''
                  }
                  placement="top"
                  hasArrow
                  isDisabled={!company.div_cidade && !company.div_uf}
                >
                  <Flex
                    align="center"
                    gap={1}
                    bg={company.div_cidade || company.div_uf ? 'red.50' : 'green.50'}
                    px={2}
                    py={1}
                    borderRadius="md"
                    border={company.div_cidade || company.div_uf ? '1px dashed' : 'none'}
                    borderColor="red.300"
                  >
                    <Icon
                      as={company.div_cidade || company.div_uf ? WarningCircle : MapPin}
                      color={company.div_cidade || company.div_uf ? 'red.500' : 'green.500'}
                      boxSize={3}
                      weight={company.div_cidade || company.div_uf ? 'fill' : 'regular'}
                    />
                    <Text
                      fontSize="xs"
                      color={company.div_cidade || company.div_uf ? 'red.700' : 'green.700'}
                      fontWeight="medium"
                      noOfLines={1}
                    >
                      {[company.cidade, company.uf].filter(Boolean).join(' - ')}
                    </Text>
                  </Flex>
                </Tooltip>
              )}

              {/* Campos Dinâmicos - usando campos_valores da listagem */}
              {fieldValues.length > 0 &&
                fieldValues.map((fv: ICompanyFieldValueSimple) => (
                  <Tooltip
                    key={`${fv.campo}-${fv.value}`}
                    label={`${fv.campo.replace(/_/g, ' ')}: ${fv.value}`}
                    placement="top"
                  >
                    <Flex align="center" gap={1} bg="orange.50" px={2} py={1} borderRadius="md">
                      <Icon as={Star} color="orange.500" boxSize={3} weight="fill" />
                      <Text fontSize="xs" color="orange.700" fontWeight="medium" noOfLines={1}>
                        {fv.value}
                      </Text>
                    </Flex>
                  </Tooltip>
                ))}
            </Flex>
          </Box>
        </Box>

        {/* Coluna de ações */}
        {hasActionButtons && (
          <Flex
            flexDirection="column"
            alignItems="center"
            justifyContent="flex-start"
            gap={1}
            py={2}
            px={1}
            bg="gray.50"
            borderLeft="1px"
            borderColor="gray.200"
            w="40px"
            minW="40px"
            flexShrink={0}
            alignSelf="stretch"
          >
            {/* Botão de anotações */}
            {showNotesButton && (
              <ViewNotaModal
                id={company.id}
                dateStart={dateStart}
                dateEnd={dateEnd}
                defaultTab="empresas"
                companyName={company.razao_social || company.nome}
              >
                <Box as="span">
                  <Tooltip label="Anotações" placement="left">
                    <IconButton
                      aria-label="Anotações"
                      icon={<Icon as={NotePencil} weight="fill" boxSize={4} />}
                      size="xs"
                      variant="ghost"
                      borderRadius="md"
                      color="purple.500"
                      _hover={{ bg: 'purple.100', color: 'purple.600' }}
                    />
                  </Tooltip>
                </Box>
              </ViewNotaModal>
            )}

            {/* Botão de editar características */}
            {onEditCharacteristics && (
              <Tooltip label="Editar características" placement="left">
                <IconButton
                  aria-label="Editar características"
                  icon={<Icon as={PencilSimple} weight="fill" boxSize={4} />}
                  size="xs"
                  variant="ghost"
                  borderRadius="md"
                  color="blue.500"
                  onClick={handleEditClick}
                  _hover={{ bg: 'blue.100', color: 'blue.600' }}
                />
              </Tooltip>
            )}

            {/* Botão de Dashboard */}
            {showDashboardButton && (
              <Tooltip label="Dashboard" placement="left">
                <IconButton
                  aria-label="Dashboard"
                  icon={<Icon as={ChartBar} weight="fill" boxSize={4} />}
                  size="xs"
                  variant="ghost"
                  borderRadius="md"
                  color="teal.500"
                  onClick={handleDashboardClick}
                  _hover={{ bg: 'teal.100', color: 'teal.600' }}
                />
              </Tooltip>
            )}

            {/* Botão de Dossiê */}
            {showDossieButton && (
              <Tooltip label="Ver Dossiê" placement="left">
                <IconButton
                  aria-label="Ver Dossiê"
                  icon={<Icon as={FolderOpen} weight="fill" boxSize={4} />}
                  size="xs"
                  variant="ghost"
                  borderRadius="md"
                  color="orange.500"
                  onClick={handleDossieClick}
                  isDisabled={!company?.external_id}
                  _hover={{ bg: 'orange.100', color: 'orange.600' }}
                />
              </Tooltip>
            )}
          </Flex>
        )}
      </Flex>

      {/* Modal de Dashboard da Empresa */}
      <EmpresaDetailModal isOpen={isDashboardOpen} onClose={onDashboardClose} empresa={company} />
    </Box>
  );
}
